import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { confirmPasswordReset } from 'firebase/auth';
import { auth, storage } from '../../firebase/firebase';
import { TextField, Button, Container, Typography, Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getCurrentBusinessConfig } from '../../firebase/firebase-delegate';
import { getDownloadURL, ref } from 'firebase/storage';

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [isLogoReady, setIsLogoReady] = useState(false);
    const navigate = useNavigate();



    const oobCode = searchParams.get('oobCode');

    const businessConfigQuery = useQuery({
        queryKey: ['businessConfig'],
        queryFn: () => getCurrentBusinessConfig(),
    });

    useEffect(() => {
        const loadLogo = async () => {
            const logoImagePath = businessConfigQuery.data?.businessInfo?.logoImagePath;
            if (!logoImagePath) return;

            try {
                const logoDownloadURL = await getDownloadURL(ref(storage, logoImagePath));
                const img = new Image();
                img.src = logoDownloadURL;
                img.onload = () => {
                    setLogoUrl(logoDownloadURL);
                    setIsLogoReady(true);
                };
            } catch (error) {
                console.error("Failed to load logo image:", error);
            }
        };

        if (businessConfigQuery.data?.businessInfo?.logoImagePath) {
            loadLogo();
        }
    }, [businessConfigQuery.data?.businessInfo?.logoImagePath]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            await confirmPasswordReset(auth, oobCode!, newPassword);
            setSuccess(true);
            setTimeout(() => navigate('/admin'), 3000); // Redirect after success
        } catch (err) {
            console.error(err);
            setError('Invalid or expired reset link');
        }
    };

    if (!oobCode) {
        return <Typography>Missing reset token.</Typography>;
    }


    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            {isLogoReady && logoUrl && (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    mb={3}
                    sx={{
                        backgroundColor: 'black',
                        borderRadius: 2,
                        padding: 2,
                        width: 'fit-content',
                        mx: 'auto',
                    }}
                >
                    <img
                        src={logoUrl}
                        alt="Logo"
                        style={{ height: 80, objectFit: 'contain', backgroundColor: 'transparent' }}
                    />
                </Box>
            )}
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Reset Your Password
            </Typography>
            {error && <Typography color="error">{error}</Typography>}
            {success ? (
                <Typography color="success.main">Password reset successful! Please wait while we redirect you back...</Typography>
            ) : (
                <form onSubmit={handleSubmit}>
                    <TextField
                        label="New Password"
                        type="password"
                        fullWidth
                        sx={{ mb: 2 }}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <TextField
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        sx={{ mb: 2 }}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <Button variant="contained" type="submit">
                        Reset Password
                    </Button>
                </form>
            )}
        </Container>
    );
};

export default ResetPasswordPage;
